import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BookOpen, Plus, Trash } from '@phosphor-icons/react';
import ContextHelpButton from '../ContextHelpButton';

// PRODUCT-IA-CLEANUP-001A: extracted from the historical `personas` tab
// inline JSX in ProjectView.js — this is the Tone of Voice contextual
// destination inside the DNA workspace. Self-contained (own load of
// `/tov/{project.id}` and `/tov-library`), reusing the exact same
// endpoints as before.
export default function ToneOfVoiceSection({ api, project }) {
  const { t } = useTranslation();
  const [tov, setTov] = useState({});
  const [tovLibrary, setTovLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTovSave, setShowTovSave] = useState(false);
  const [tovSaveName, setTovSaveName] = useState('');
  const [savingTovLibrary, setSavingTovLibrary] = useState(false);
  const [applyingTovId, setApplyingTovId] = useState(null);

  useEffect(() => {
    if (!project?.id) return;
    setLoading(true);
    Promise.all([
      api.get(`/tov/${project.id}`).then(r => setTov(r.data || {})).catch(() => {}),
      api.get('/tov-library').then(r => setTovLibrary(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const saveToTovLibrary = async () => {
    if (!tovSaveName.trim()) return;
    setSavingTovLibrary(true);
    const tid = toast.loading(t('project.tov.saving'));
    try {
      const { data } = await api.post('/tov-library', { name: tovSaveName, ...tov });
      setTovLibrary(prev => [...prev, data]);
      setTovSaveName('');
      setShowTovSave(false);
      toast.success(t('project.tov.saveSuccess'), { id: tid });
    } catch (e) { toast.error(t('project.tov.saveError'), { id: tid }); } finally { setSavingTovLibrary(false); }
  };

  const applyTovTemplate = async (itemId) => {
    setApplyingTovId(itemId);
    const tid = toast.loading(t('project.tov.applying'));
    try {
      await api.post(`/tov-library/${itemId}/apply/${project.id}`);
      const { data } = await api.get(`/tov/${project.id}`);
      setTov(data || {});
      toast.success(t('project.tov.applySuccess'), { id: tid });
    } catch (e) { toast.error(t('project.tov.applyError'), { id: tid }); } finally { setApplyingTovId(null); }
  };

  const deleteTovTemplate = (itemId) => {
    api.delete(`/tov-library/${itemId}`);
    setTovLibrary(prev => prev.filter(item => item.id !== itemId));
  };

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)] py-8">{t('common.loading')}</p>;
  }

  // PRODUCT-IA-CLEANUP-001B (task section 2): `tov` is already the exact
  // GET /tov/{project_id} payload — the backend projection already
  // excludes `_id` (see backend/legacy_monolith.py get_tov), but we still
  // never render `_id`/`project_id`/`user_id`/`updated_at` defensively
  // here, even if the API response shape changes later.
  const hasCurrentTov = Object.keys(tov || {}).length > 0;
  const sliderFields = ['formality', 'energy', 'empathy', 'humor', 'storytelling'];
  const captionLength = tov.caption_length;
  const listFields = [
    { key: 'custom_instructions', labelKey: 'wizard.tov.instructionsLabel' },
    { key: 'brand_keywords', labelKey: 'wizard.tov.brandKeywordsLabel' },
    { key: 'forbidden_words', labelKey: 'wizard.tov.forbiddenWordsLabel' },
    { key: 'signature_phrases', labelKey: 'wizard.tov.signaturePhrasesLabel' },
  ].filter(f => (tov[f.key] || '').toString().trim().length > 0);

  return (
    <div className="pt-2 pb-10">
      <div className="mb-6">
        <div className="mb-1 flex items-center justify-between gap-2">
          <h3 className="font-semibold flex items-center gap-2 text-sm md:text-base"><BookOpen size={18} /> {t('project.tov.current.title')}</h3>
          <ContextHelpButton articleId="tone-of-voice" label="Guida Tone of Voice" />
        </div>
        <p className="text-xs text-[var(--text-muted)] mb-3">{t('project.tov.current.subtitle')}</p>
        {hasCurrentTov ? (
          <div className="card p-4 space-y-4" data-testid="current-tov-card">
            <div className="flex flex-wrap gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{t('project.tov.current.presetLabel')}</p>
                <p className="text-sm font-medium">{tov.preset ? t(`wizard.presets.${tov.preset}`, tov.preset) : t('project.tov.current.presetCustom')}</p>
              </div>
              {captionLength && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{t('wizard.tov.captionLengthLabel')}</p>
                  <p className="text-sm font-medium">{t(`wizard.tov.captionLengths.${captionLength}.label`, captionLength)}</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {sliderFields.map(field => (
                <div key={field}>
                  <p className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{t(`wizard.tov.sliders.${field}.label`)}</p>
                  <p className="text-sm font-medium">{tov[field]}/10</p>
                </div>
              ))}
            </div>
            {listFields.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-[var(--border-color)]">
                {listFields.map(f => (
                  <div key={f.key}>
                    <p className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{t(f.labelKey)}</p>
                    <p className="text-xs whitespace-pre-wrap">{tov[f.key]}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="card p-4 text-center" data-testid="current-tov-empty">
            <p className="text-sm font-medium">{t('project.tov.current.emptyTitle')}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">{t('project.tov.current.emptyDescription')}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-semibold flex items-center gap-2 text-sm md:text-base"><BookOpen size={18} /> {t('project.tov.title')}</h3>
          <p className="text-xs text-[var(--text-muted)] mt-1">{t('project.tov.subtitle')}</p>
        </div>
        <button className="btn-gradient text-xs" onClick={() => setShowTovSave(true)}><Plus size={14} /> {t('project.tov.saveCurrent')}</button>
      </div>
      {showTovSave && (
        <div className="card mb-4 p-4">
          <div className="flex gap-2 items-end">
            <input className="input-dark text-sm py-2 flex-1" placeholder={t('project.tov.templateName')} value={tovSaveName} onChange={e => setTovSaveName(e.target.value)} style={{ paddingLeft: '0.75rem' }} />
            <button className="btn-gradient text-xs py-2" onClick={saveToTovLibrary} disabled={savingTovLibrary}>
              {savingTovLibrary ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-1" />{t('project.tov.saving')}</> : t('common.save')}
            </button>
            <button className="btn-ghost text-xs py-2" onClick={() => setShowTovSave(false)}>{t('common.close')}</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {tovLibrary.map(item => (
          <div key={item.id} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{item.name}</p>
              <p className="text-[10px] text-[var(--text-muted)]">F:{item.formality} E:{item.energy} Em:{item.empathy} H:{item.humor}</p>
            </div>
            <button className="btn-gradient text-[10px] py-1 px-2" onClick={() => applyTovTemplate(item.id)} disabled={applyingTovId === item.id}>
              {applyingTovId === item.id ? <div className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin" /> : t('project.tov.apply')}
            </button>
            <button className="text-[var(--accent-pink)]" onClick={() => deleteTovTemplate(item.id)}><Trash size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
